import { inject, Injectable, DestroyRef } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HttpContext, HttpErrorResponse } from "@angular/common/http";
import { Router } from "@angular/router";
import {
  BehaviorSubject,
  delay,
  first,
  map,
  Observable,
  retry,
  shareReplay,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timeout,
  withLatestFrom,
} from "rxjs";
import { CognitoUtilsService } from "../../auth/services/cognito-utils.service";
import { LogoutEventService } from "../../auth/services/logout-event.service";
import { SessionExpiredError } from "../../auth/errors/session-expired.error";
import { GenderType, User, UserRole } from "../interfaces/user.interface";
import { Tenant, mapTenantFromDto } from "../interfaces/tenant.interface";
import {
  UsersUsersService,
  UsersUserUpdateDto,
  UsersTenantsService,
  UsersTenantResponseDto,
} from "@o2-fe/api";
import {
  SUPPRESS_ERROR_TOAST,
  SUPPRESS_SUCCESS_TOAST,
} from "@o2-fe/toast-message";

/**
 * Service for managing logged-in user and tenant data.
 * Handles user profile loading, updates, and state management.
 */
@Injectable({
  providedIn: "root",
})
export class LoggedUserService {
  /** Service for Cognito utilities and user identifiers */
  private readonly _cognitoUtilsService = inject(CognitoUtilsService);

  /** Generated API service for users from o2-users microservice */
  private readonly _usersService = inject(UsersUsersService);

  /** Generated API service for tenants from o2-users microservice */
  private readonly _tenantsService = inject(UsersTenantsService);

  /** Angular router for navigation */
  private readonly _router = inject(Router);

  /** Logout event service for auto-clearing on logout */
  private readonly _logoutEventService = inject(LogoutEventService);

  /** Destroy reference for cleanup */
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    // Auto-clear user/tenant state on logout with delay.
    // The 500ms delay allows AuthService to complete navigation to login page
    // before clearing state, preventing UI flash (permissions/icons disappearing
    // momentarily while still on the authenticated page).
    this._logoutEventService.onLogout$
      .pipe(delay(500), takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this.clearUser());
  }

  /** The user subject */
  private _userSubject = new BehaviorSubject<User | null>(null);

  /** The tenant subject */
  private _tenantSubject = new BehaviorSubject<Tenant | null>(null);

  /**
   * Pure user observable directly from BehaviorSubject
   * Use this for creating signals (not the complex user$ observable)
   */
  public readonly userSubject$: Observable<User | null> =
    this._userSubject.asObservable();

  /**
   * Pure tenant observable directly from BehaviorSubject
   * Use this for creating signals (not the complex tenant$ observable)
   */
  public readonly tenantSubject$: Observable<Tenant | null> =
    this._tenantSubject.asObservable();

  /** Loading state for user data */
  private _loadingSubject: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);

  /** Flag indicating a load error occurred (user/tenant not found) */
  private _hasLoadError = false;

  /**
   * Flag to enable polling during trial initialization.
   * When true, _loadUserFromBe() and _loadTenantFromBe() will use retry logic.
   * Automatically reset after the first successful load.
   */
  private _trialPollingEnabled = false;

  /** Subject to complete observables on logout */
  private _destroySubject = new Subject<void>();

  /** Loading state observable */
  public readonly loading$: Observable<boolean> =
    this._loadingSubject.asObservable();

  /** Check if there's a load error */
  public get hasLoadError(): boolean {
    return this._hasLoadError;
  }

  /**
   * Enables trial polling mode.
   * Called by AuthService after trial signup before navigation.
   * The load methods will use retry instead of failing immediately.
   */
  public enableTrialPolling(): void {
    this._trialPollingEnabled = true;
  }

  /**
   * Disables trial polling mode.
   * Called automatically after the first successful load.
   */
  private _disableTrialPolling(): void {
    this._trialPollingEnabled = false;
  }

  /**
   * Public user observable with auto-loading and caching
   * Uses shareReplay to share the subscription between multiple subscribers
   * Completes on logout via _destroySubject
   */
  public user$: Observable<User | null> = this._createUserObservable();

  /**
   * Public tenant observable with auto-loading and caching
   * Uses shareReplay to share the subscription between multiple subscribers
   * Completes on logout via _destroySubject
   */
  public tenant$: Observable<Tenant | null> = this._createTenantObservable();

  /**
   * Creates the user observable with proper cleanup
   */
  private _createUserObservable(): Observable<User | null> {
    return this._userSubject.pipe(
      switchMap((user) =>
        user ? this._userSubject.asObservable() : this._loadUserFromBe(),
      ),
      takeUntil(this._destroySubject),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  /**
   * Creates the tenant observable with proper cleanup
   */
  private _createTenantObservable(): Observable<Tenant | null> {
    return this._tenantSubject.pipe(
      switchMap((tenant) =>
        tenant ? this._tenantSubject.asObservable() : this._loadTenantFromBe(),
      ),
      takeUntil(this._destroySubject),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  /**
   * Force refresh user data from backend
   */
  public refreshUser(): void {
    this._userSubject.next(null);
  }

  /**
   * Force refresh tenant data from backend
   */
  public refreshTenant(): void {
    this._tenantSubject.next(null);
  }

  /**
   * Update tenant data without triggering a reload
   * @param tenant - Updated tenant data (from API)
   */
  public updateTenant(tenant: UsersTenantResponseDto): void {
    this._tenantSubject.next(mapTenantFromDto(tenant));
  }

  /**
   * Update user data without triggering a reload.
   * Used when we already have the updated user data (e.g., from API response).
   * @param user - Updated user data (from API)
   */
  public updateUser(user: User): void {
    // Keep `roles` token-sourced even for an in-session update (e.g. an admin
    // editing their own record): a Mongo role change must not take effect until
    // the token is refreshed on the next login. `getUserRoles()` completes and
    // fails closed to `[]`, so no leak and no unsafe fallback.
    this._cognitoUtilsService
      .getUserRoles()
      .pipe(first())
      .subscribe((roles) =>
        this._userSubject.next({ ...user, roles: roles as User["roles"] }),
      );
  }

  /**
   * Checks if user profile is completed
   * @returns true if profile is completed, false otherwise
   */
  public isProfileCompleted(): Observable<boolean> {
    return this.user$.pipe(map((user) => this._innerIsProfileCompleted(user)));
  }

  /**
   * Check if user has all specified roles
   * @param roles - Roles to check
   * @returns Observable that emits true if user has all roles
   */
  hasRoles(...roles: UserRole[]): Observable<boolean> {
    return this._cognitoUtilsService.getUserIdentifiers().pipe(
      first(),
      map(({ groups }) => roles.every((role) => groups.includes(role))),
    );
  }

  /**
   * Complete user profile with additional information
   * @param name - User's first name
   * @param surname - User's last name
   * @param gender - User's gender
   * @param phone - User's phone number
   * @returns Observable that completes when profile is updated
   */
  completeProfile(
    name: string,
    surname: string,
    gender: GenderType | undefined,
    phone: string,
  ): Observable<void> {
    return this._cognitoUtilsService.getUserIdentifiers().pipe(
      withLatestFrom(this.user$),
      switchMap(([claims, user]) => {
        const updateDto: UsersUserUpdateDto = {
          name,
          surname,
          gender,
          phone,
          address: user?.address,
          birthday: user?.birthday,
          code: user?.code,
          hourlyWage: user?.hourlyWage,
          lang: user?.lang,
          profilePicUrl: user?.profilePicUrl,
          roles: user?.roles,
          status: user?.status,
          timezone: user?.timezone,
        };
        return this._usersService
          .usersControllerUpdate({
            tenant: claims.tenant,
            id: claims.sub,
            body: updateDto,
          })
          .pipe(map((updated) => this._withTokenRoles(updated, claims.groups)));
      }),
      map((user) => {
        this._userSubject.next(user);
      }),
    );
  }

  /**
   * Mark the user as having accessed the app (welcome flow done).
   * Sets `hasAccessed: true` server-side and refreshes the local
   * user subject with the response.
   */
  markAccessed(): Observable<User> {
    // Suppress the generic "user updated" toast — the user just
    // dismissed the welcome modal, surfacing a success toast for
    // that bookkeeping write would feel like a system notification
    // they didn't ask for.
    const context = new HttpContext().set(SUPPRESS_SUCCESS_TOAST, true);
    return this._cognitoUtilsService.getUserIdentifiers().pipe(
      switchMap(({ sub, tenant, groups }) =>
        this._usersService
          .usersControllerUpdate(
            {
              tenant,
              id: sub,
              body: { hasAccessed: true },
            },
            context,
          )
          .pipe(map((updated) => this._withTokenRoles(updated, groups))),
      ),
      map((updatedUser) => {
        this._userSubject.next(updatedUser);
        return updatedUser;
      }),
    );
  }

  /**
   * Update user profile
   * @param updates - Partial user data to update (name, surname, phone)
   * @returns Observable that completes when profile is updated
   */
  updateProfile(
    updates: Partial<Pick<User, "name" | "surname" | "phone">>,
  ): Observable<User> {
    return this._cognitoUtilsService.getUserIdentifiers().pipe(
      switchMap(({ sub, tenant, groups }) => {
        const updateDto: UsersUserUpdateDto = {};

        if (updates.name !== undefined) {
          updateDto.name = updates.name;
        }

        if (updates.surname !== undefined) {
          updateDto.surname = updates.surname;
        }

        if (updates.phone !== undefined) {
          updateDto.phone = updates.phone;
        }

        return this._usersService
          .usersControllerUpdate({
            tenant: tenant,
            id: sub,
            body: updateDto,
          })
          .pipe(map((updated) => this._withTokenRoles(updated, groups)));
      }),
      map((updatedUser) => {
        this._userSubject.next(updatedUser);
        return updatedUser;
      }),
    );
  }

  /**
   * Clear user and tenant data (on logout)
   * Completes all observables and recreates them
   */
  clearUser(): void {
    // Complete all existing observables
    this._destroySubject.next();
    this._destroySubject.complete();

    // Reset all subjects
    this._userSubject.next(null);
    this._tenantSubject.next(null);
    this._loadingSubject.next(false);
    this._hasLoadError = false;

    // Recreate the destroy subject and observables
    this._destroySubject = new Subject<void>();
    this.user$ = this._createUserObservable();
    this.tenant$ = this._createTenantObservable();
  }

  /**
   * Overrides a Mongo-loaded user's `roles` with the roles from the Cognito
   * token. This service is the single place that merges the two sources: every
   * other field (name, wage, status, ...) stays as loaded from Mongo, while
   * `roles` — the authorization input — always comes from the token, so the
   * whole app agrees with the backend guard (which reads the same claim) and no
   * consumer reading `user.roles` can diverge. The caller passes the `groups` it
   * already resolved from the token in the same stream, so no extra call is made.
   *
   * @param user - The user as returned by the o2-users API (Mongo record).
   * @param groups - Raw `cognito:groups` from the token read alongside the user.
   * @returns The user with `roles` replaced by the token's normalized roles.
   */
  private _withTokenRoles(user: User, groups: string[]): User {
    return {
      ...user,
      roles: this._cognitoUtilsService.rolesFromGroups(groups) as User["roles"],
    };
  }

  /**
   * Internal check for profile completion
   * @param user - User to check
   * @returns true if profile is completed
   */
  private _innerIsProfileCompleted(user: User | null): boolean {
    return Boolean(user?.name && user?.surname && user.gender);
  }

  /**
   * Load user data from backend.
   * If _trialPollingEnabled is true, retries up to 15 times with 1 second intervals.
   * @returns Observable of user data
   */
  private _loadUserFromBe(): Observable<User | null> {
    this._loadingSubject.next(true);

    // Use SUPPRESS_ERROR_TOAST during trial polling to avoid showing 404 toasts
    const context = this._trialPollingEnabled
      ? new HttpContext().set(SUPPRESS_ERROR_TOAST, true)
      : undefined;

    const loadObservable = this._cognitoUtilsService.getUserIdentifiers().pipe(
      switchMap(({ sub, tenant, groups }) => {
        return this._usersService
          .usersControllerFindOne({ tenant: tenant, id: sub }, context)
          .pipe(map((user) => this._withTokenRoles(user, groups)));
      }),
    );

    // If polling enabled, use retry logic
    const withRetry = this._trialPollingEnabled
      ? loadObservable.pipe(retry({ count: 15, delay: 1000 }), timeout(20000))
      : loadObservable;

    return withRetry.pipe(
      map((user) => {
        this._userSubject.next(user);
        this._loadingSubject.next(false);
        this._disableTrialPolling(); // Disable after success
        return user;
      }),
      switchMap(() => this.user$),
      tap({
        error: (error: Error) => {
          this._disableTrialPolling();
          this._handleLoadError(error);
        },
      }),
    );
  }

  /**
   * Load tenant data from backend.
   * If _trialPollingEnabled is true, retries up to 15 times with 1 second intervals.
   * @returns Observable of tenant data
   */
  private _loadTenantFromBe(): Observable<Tenant | null> {
    this._loadingSubject.next(true);

    // Use SUPPRESS_ERROR_TOAST during trial polling to avoid showing 404 toasts
    const context = this._trialPollingEnabled
      ? new HttpContext().set(SUPPRESS_ERROR_TOAST, true)
      : undefined;

    // Use name to retrocompatibility
    const loadObservable = this._cognitoUtilsService.getUserIdentifiers().pipe(
      switchMap(({ tenant }) => {
        return this._tenantsService.tenantsControllerFindByName(
          { name: tenant },
          context,
        );
      }),
    );

    // If polling enabled, use retry logic
    const withRetry = this._trialPollingEnabled
      ? loadObservable.pipe(retry({ count: 15, delay: 1000 }), timeout(20000))
      : loadObservable;

    return withRetry.pipe(
      map((tenantDto) => {
        const mappedTenant = mapTenantFromDto(tenantDto);
        this._tenantSubject.next(mappedTenant);
        this._loadingSubject.next(false);
        // Don't disable here - let _loadUserFromBe handle it
        return mappedTenant;
      }),
      switchMap(() => this.tenant$),
      tap({
        error: (error: Error) => {
          this._handleLoadError(error);
        },
      }),
    );
  }

  /**
   * Centralised error handling for user/tenant loading.
   *
   * Distinguishes a dead session from a genuine "authenticated but not found"
   * error so the two no longer navigate to competing routes (which caused the
   * app to hang on a blank page):
   * - No Cognito session at all: the auth guard / login flow owns the redirect.
   * - Session expired (Cognito refresh failure) or a backend 401: the session
   *   is dead, so trigger a logout to return cleanly to the login page.
   * - Any other backend error: the user is authenticated but the record could
   *   not be loaded, so show the generic error page.
   *
   * @param error - The error thrown while loading user or tenant data.
   */
  private _handleLoadError(error: Error): void {
    this._loadingSubject.next(false);

    // No Cognito session at all — the auth guard / login flow handles it.
    if (error?.message === "CognitoUserSession null") {
      return;
    }

    // The session is dead (refresh token expired) or the backend rejected the
    // token with a 401: log out instead of showing the error page. We check
    // specifically for the session-expired signal — not merely "not an
    // HttpErrorResponse" — so unrelated non-HTTP errors (e.g. an RxJS
    // TimeoutError raised by the trial-signup polling) still reach /error.
    if (
      error instanceof SessionExpiredError ||
      (error instanceof HttpErrorResponse && error.status === 401)
    ) {
      this._logoutEventService.emitLogout();
      return;
    }

    // Authenticated, but the user/tenant genuinely could not be loaded.
    this._hasLoadError = true;
    void this._router.navigate(["/error"]);
  }
}
